(() => {
  const initializeHeroVideo = (root) => {
    if (!root || root.dataset.aliroVideoInitialized === 'true') return;

    const videos = Array.from(root.querySelectorAll('.aliro-hero__video'));
    if (!videos.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    const syncPlayback = () => {
      if (reducedMotion.matches) {
        videos.forEach((video) => {
          video.pause();
          video.removeAttribute('autoplay');
        });
        root.classList.remove('has-playing-video');
        root.classList.add('has-video-fallback');
        return;
      }

      root.classList.remove('has-video-fallback');
      videos.forEach((video) => {
        video.muted = true;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => root.classList.add('has-video-fallback'));
        }
      });
    };

    videos.forEach((video) => {
      video.addEventListener('playing', () => {
        root.classList.add('has-playing-video');
        root.classList.remove('has-video-fallback');
      });
      video.addEventListener('error', () => root.classList.add('has-video-fallback'));
    });

    reducedMotion.addEventListener?.('change', syncPlayback);
    syncPlayback();
    root.dataset.aliroVideoInitialized = 'true';
  };

  const initializeSelector = (root) => {
    if (!root || root.dataset.aliroInitialized === 'true') return;

    const dataNode = root.querySelector('[data-aliro-products]');
    if (!dataNode) return;

    let products;
    try {
      products = JSON.parse(dataNode.textContent);
    } catch (error) {
      return;
    }

    const doorStep = root.querySelector('[data-aliro-door-step]');
    const entryStep = root.querySelector('[data-aliro-entry-step]');
    const result = root.querySelector('[data-aliro-result]');
    const placeholder = root.querySelector('[data-aliro-placeholder]');
    const image = root.querySelector('[data-aliro-result-image]');
    const match = root.querySelector('[data-aliro-result-match]');
    const label = root.querySelector('[data-aliro-result-label]');
    const name = root.querySelector('[data-aliro-result-name]');
    const hook = root.querySelector('[data-aliro-result-hook]');
    const explainer = root.querySelector('[data-aliro-result-explainer]');
    const proof = root.querySelector('[data-aliro-result-proof]');
    const link = root.querySelector('[data-aliro-result-link]');
    const resultButtonLabel = root.dataset.resultButtonLabel || 'View this lock';
    const alternative = root.querySelector('[data-aliro-result-alternative]');
    const progressOne = root.querySelector('[data-aliro-progress="1"]');
    const progressTwo = root.querySelector('[data-aliro-progress="2"]');
    const liveRegion = root.querySelector('[data-aliro-live]');
    let selectedDoor = '';

    const trackedProductUrl = (url, productKey) => {
      const output = new URL(url, window.location.origin);
      output.searchParams.set('utm_source', 'aliro_hub');
      output.searchParams.set('utm_medium', 'model_selector');
      output.searchParams.set('utm_campaign', 'phone_as_key_launch');
      output.searchParams.set('selector_result', productKey);
      return output.toString();
    };

    const showDoorStep = () => {
      selectedDoor = '';
      doorStep.hidden = false;
      entryStep.hidden = true;
      result.hidden = true;
      placeholder.hidden = false;
      image.hidden = true;
      image.removeAttribute('src');
      image.alt = '';
      match.hidden = true;
      match.textContent = '';
      progressOne.className = 'is-active';
      progressTwo.className = '';
      liveRegion.textContent = 'Question one of two. Choose a door location.';
      root.querySelector('[data-aliro-door]').focus({ preventScroll: true });
    };

    const showEntryStep = (doorKey) => {
      selectedDoor = doorKey;
      doorStep.hidden = true;
      entryStep.hidden = false;
      result.hidden = true;
      placeholder.hidden = false;
      progressOne.className = 'is-complete';
      progressTwo.className = 'is-active';
      liveRegion.textContent = 'Question two of two. Choose an entry experience.';
      root.querySelector('[data-aliro-entry]').focus({ preventScroll: true });
    };

    const resolveProduct = (doorKey, entryKey) => {
      if (doorKey === 'lever' || doorKey === 'shared') return 'latch';
      if (entryKey === 'handsfree') return 'mission';
      return 'pro';
    };

    const showResult = (productKey) => {
      const product = products[productKey];
      if (!product) return;

      doorStep.hidden = true;
      entryStep.hidden = true;
      placeholder.hidden = true;
      result.hidden = false;
      if (product.image) {
        image.src = product.image;
        image.alt = product.alt;
        image.hidden = false;
      } else {
        image.removeAttribute('src');
        image.alt = '';
        image.hidden = true;
      }
      match.textContent = product.label;
      match.hidden = false;
      label.textContent = product.kicker;
      name.textContent = product.name;
      hook.textContent = product.hook;
      explainer.textContent = product.explainer;
      proof.innerHTML = '';
      product.proof.forEach((item) => {
        const listItem = document.createElement('li');
        listItem.textContent = item;
        proof.appendChild(listItem);
      });
      link.href = trackedProductUrl(product.url, productKey);
      link.textContent = resultButtonLabel;
      alternative.textContent = '';
      if (product.alternative && product.alternativeName && product.alternativeUrl) {
        alternative.append(`${product.alternative} `);
        const alternativeLink = document.createElement('a');
        alternativeLink.href = trackedProductUrl(product.alternativeUrl, productKey);
        alternativeLink.textContent = product.alternativeName;
        alternative.appendChild(alternativeLink);
        alternative.append('.');
      }
      progressOne.className = 'is-complete';
      progressTwo.className = 'is-complete';
      liveRegion.textContent = `Your recommended lock is ${product.name}. ${product.hook}`;
      result.focus({ preventScroll: true });
    };

    root.querySelectorAll('[data-aliro-door]').forEach((button) => {
      button.addEventListener('click', () => {
        showEntryStep(button.dataset.aliroDoor);
      });
    });

    root.querySelectorAll('[data-aliro-entry]').forEach((button) => {
      button.addEventListener('click', () => {
        showResult(resolveProduct(selectedDoor, button.dataset.aliroEntry));
      });
    });

    root.querySelector('[data-aliro-back]').addEventListener('click', showDoorStep);
    root.querySelectorAll('[data-aliro-reset]').forEach((button) => button.addEventListener('click', showDoorStep));
    root.dataset.aliroInitialized = 'true';
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('.aliro-hero').forEach(initializeHeroVideo);
    scope.querySelectorAll('[data-aliro-selector]').forEach(initializeSelector);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
